import React, { useEffect, useRef, useState } from 'react';

const VimeoPlayer = ({ videoId, onTimeUpdate, onReady, onError, autoPlay = false }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Load Vimeo Player API if not already loaded
    if (!window.Vimeo) {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = () => {
        initializePlayer();
      };
      document.head.appendChild(script);
    } else {
      initializePlayer();
    }

    function initializePlayer() {
      if (containerRef.current && videoId) {
        // Create iframe element
        const iframe = document.createElement('iframe');
        const params = new URLSearchParams({ api: '1', player_id: `vimeo_${videoId}` });
        if (autoPlay) {
          params.set('autoplay', '1');
        }
        iframe.src = `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.border = 'none';
        iframe.style.borderRadius = '8px';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        
        // Clear container and add iframe
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(iframe);

        // Initialize Vimeo Player
        playerRef.current = new window.Vimeo.Player(iframe);

        playerRef.current.ready().then(() => {
          setIsReady(true);
          if (onReady) onReady();
          if (autoPlay && playerRef.current) {
            try {
              playerRef.current.play();
            } catch (_) {}
          }

          // Start time tracking
          const interval = setInterval(() => {
            if (playerRef.current) {
              playerRef.current.getCurrentTime().then((currentTime) => {
                if (onTimeUpdate) onTimeUpdate(currentTime);
              }).catch(() => {
                // Handle error silently
              });
            }
          }, 100); // Update every 100ms for smooth annotation display

          // Store interval reference for cleanup
          playerRef.current.timeInterval = interval;
        }).catch((error) => {
          if (onError) onError(error);
        });

        // Handle errors
        playerRef.current.on('error', (error) => {
          if (onError) onError(error);
        });
      }
    }

    return () => {
      // Cleanup
      if (playerRef.current && playerRef.current.timeInterval) {
        clearInterval(playerRef.current.timeInterval);
      }
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, onTimeUpdate, onReady, onError, autoPlay]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '400px',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    />
  );
};

export default VimeoPlayer;
